// Vocal-Timing: Silbenanfänge erkennen, aufs Raster oder auf eine Leitspur (Lead-Vocal) ziehen.
// Gesang hat weiche Einsätze, deshalb eine eigene Erkennung über die Hüllkurve (Beat Detective sucht harte Anschläge).
// Das Ausrichten auf eine Leitspur arbeitet wie VocAlign: Klangmerkmale je 10 ms, Dynamic Time Warping im Band
// um die Diagonale, daraus eine Zeitkarte. Gezogen wird mit Elastic Audio (Warp-Marker), nicht geschnitten.
// Reine Funktionen auf Mono-Signalen in Timeline-Zeit (x[0] liegt bei t0), testbar ohne Browser.
import type { Project } from '@srs/shared';
import { fft } from '../audio/stretch.ts';
import { snapGrid } from './ops.ts';

/**
 * Signal über eine Zeitkarte lesen (Timeline → Quelle), ohne die Tonhöhe zu ändern: überlappende Körner (Hann, 50 %),
 * jedes Korn spielt die Quelle in Originalgeschwindigkeit ab der zugeordneten Stelle. Für die Analyse, nicht zum Hören.
 * toSrc liefert Quell-Sekunden oder null (dort kein Audio).
 */
export function olaRead(src: (i: number) => number, srcLen: number, srcSr: number, toSrc: (t: number) => number | null, a: number, n: number, sr: number): Float32Array {
  const H = 256, N = 2 * H, out = new Float32Array(n), w = new Float32Array(N), step = srcSr / sr;
  for (let i = 0; i < N; i++) w[i] = Math.sin((Math.PI * (i + 0.5)) / N) ** 2;
  for (let o = -H; o < n; o += H) {
    const s = toSrc(a + (o + H) / sr);
    if (s === null) continue;
    const p0 = s * srcSr - H * step;
    for (let i = 0; i < N; i++) {
      const k = o + i; if (k < 0 || k >= n) continue;
      const pos = p0 + i * step, j = Math.floor(pos), f = pos - j;
      if (j < 0 || j + 1 >= srcLen) continue;
      out[k] += w[i] * (src(j) + (src(j + 1) - src(j)) * f);
    }
  }
  return out;
}

export interface VOnset { /** Timeline-Sekunden */ t: number; /** Anstieg in dB */ s: number; /** Einsatz nach einer Pause (Phrasenanfang) */ phrase: boolean }
export interface TimingEvent { t: number; target: number }

const HOP_ON = 0.005, WIN_ON = 0.02;

/** Hüllkurve in dB (Fenster 20 ms, Schritt 5 ms), leicht geglättet */
export function envelopeDb(x: Float32Array, sr: number): Float32Array {
  const hop = Math.max(1, Math.round(HOP_ON * sr)), win = Math.round(WIN_ON * sr), n = Math.max(0, Math.floor((x.length - win) / hop) + 1);
  const e = new Float32Array(n);
  for (let k = 0; k < n; k++) { let s = 0; const a = k * hop; for (let i = a; i < a + win; i++) s += x[i] * x[i]; e[k] = 10 * Math.log10(s / win + 1e-12); }
  const out = new Float32Array(n);
  for (let k = 0; k < n; k++) out[k] = (e[Math.max(0, k - 1)] + e[k] + e[Math.min(n - 1, k + 1)]) / 3;
  return out;
}

/**
 * Silbenanfänge: nach einem Tal steigt die Hüllkurve um mindestens riseDb (Empfindlichkeit). Einsatz ist dort, wo sie
 * 15 dB unter dem Gipfel liegt (bei flachen Senken: Mitte des Anstiegs). Phrasenanfang, wenn davor Stille war.
 */
export function vocalOnsets(x: Float32Array, sr: number, t0: number, sensitivity = 50, voicing?: { f0: Float32Array; hop: number; off: number }): VOnset[] {
  const E = envelopeDb(x, sr), n = E.length, out: VOnset[] = [];
  if (!n) return out;
  let max = -Infinity; for (const v of E) if (v > max) max = v;
  // Schwelle am typischen Gesangspegel (90. Perzentil), damit Übersprechen und Rauschen in Pausen nicht zählen
  const loud = Array.from(E).filter((v) => v > max - 60).sort((a, b) => a - b), p90 = loud[Math.floor(loud.length * 0.9)] ?? max;
  const gate = Math.max(max - 45, p90 - 30, -70), rise = 12 - (Math.max(0, Math.min(100, sensitivity)) / 100) * 8;
  const frameT = (k: number) => t0 + k * HOP_ON + WIN_ON / 2;
  let valley = Infinity, vi = 0, lastT = -Infinity;
  for (let k = 0; k < n; k++) {
    if (E[k] < valley) { valley = E[k]; vi = k; }
    if (E[k] - valley < rise || E[k] < gate) continue;
    // bis zum Gipfel weiterlaufen (höchstens 120 ms)
    let pk = k; while (pk + 1 < n && pk - k < 24 && E[pk + 1] >= E[pk] - 0.3) pk++;
    // Einsatz 15 dB unter dem Gipfel (bei tiefen Tälern unabhängig von deren Tiefe), bei flachen Senken in der Mitte des Anstiegs
    const peak = E[pk], th = Math.max(peak - 15, valley + 0.5 * (peak - valley));
    let j = vi; while (j < pk && E[j + 1] < th) j++;
    const f = E[j + 1] !== E[j] ? (th - E[j]) / (E[j + 1] - E[j]) : 0, t = frameT(j + Math.max(0, Math.min(1, f)));
    if (t - lastT >= 0.08) { out.push({ t, s: peak - valley, phrase: valley < gate + 3 }); lastT = t; }
    valley = Infinity; k = pk;
  }
  // Stimmeinsätze aus der Tonhöhe: nach mindestens 30 ms stimmlos (z. B. „sh“, „s“, „f“) beginnt ein Vokal.
  // Bei schnellem Flow sind die Pegelsenken zwischen Silben flach, der Vokalbeginn ist dann der verlässlichere Einsatz.
  if (voicing) {
    const { f0, hop, off } = voicing, eAt = (t: number) => E[Math.max(0, Math.min(n - 1, Math.round((t - t0 - WIN_ON / 2) / HOP_ON)))];
    for (let k = 3; k < f0.length; k++) {
      if (!(f0[k] > 0) || f0[k - 1] > 0 || f0[k - 2] > 0 || f0[k - 3] > 0) continue;
      const t = t0 + off + k * hop - hop / 2;
      if (eAt(t + 0.02) < gate) continue;
      if (out.some((o) => Math.abs(o.t - t) < 0.08)) continue;
      out.push({ t, s: 0, phrase: false });
    }
    out.sort((a, b) => a.t - b.t);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Klangmerkmale und Dynamic Time Warping

const HOP_F = 0.01, BANDS = 24;
/**
 * Merkmale je 10 ms: Log-Bandenergien (24 Bänder 80 Hz – 8 kHz, auf den lautesten Wert bezogen, bei −60 dB begrenzt),
 * Tonhöhenklassen (Chroma, 12, unterscheidet Halbtöne) und Einsatzstärke je Band (positiver Anstieg, schärft Silbenanfänge).
 */
export function features(x: Float32Array, sr: number): Float32Array[] {
  const N = 1024, hop = Math.round(HOP_F * sr), n = Math.max(1, Math.floor(x.length / hop)), bin = sr / N;
  const w = new Float64Array(N); for (let i = 0; i < N; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
  const edges = Array.from({ length: BANDS + 1 }, (_, b) => Math.round((80 * Math.pow(8000 / 80, b / BANDS)) / bin));
  const pc = new Int8Array(N / 2).fill(-1);
  for (let k = 1; k < N / 2; k++) { const f = k * bin; if (f >= 80 && f <= 2000) pc[k] = ((Math.round(12 * Math.log2(f / 440)) % 12) + 12) % 12; }
  const re = new Float64Array(N), im = new Float64Array(N), band: Float32Array[] = [], chroma: Float32Array[] = [];
  let gmax = -Infinity;
  for (let k = 0; k < n; k++) {
    const c = k * hop - N / 2;
    for (let i = 0; i < N; i++) { const j = c + i; re[i] = j >= 0 && j < x.length ? x[j] * w[i] : 0; im[i] = 0; }
    fft(re, im);
    const v = new Float32Array(BANDS), ch = new Float32Array(12);
    for (let b = 0; b < BANDS; b++) { let s2 = 0; for (let i = Math.max(1, edges[b]); i < Math.max(edges[b] + 1, edges[b + 1]); i++) s2 += re[i] * re[i] + im[i] * im[i]; v[b] = 10 * Math.log10(s2 + 1e-12); if (v[b] > gmax) gmax = v[b]; }
    for (let i = 1; i < N / 2; i++) if (pc[i] >= 0) ch[pc[i]] += re[i] * re[i] + im[i] * im[i];
    band.push(v); chroma.push(ch);
  }
  const F: Float32Array[] = [];
  for (let k = 0; k < n; k++) {
    const v = band[k], f = new Float32Array(BANDS + 12 + BANDS);
    for (let b = 0; b < BANDS; b++) f[b] = Math.max(-60, v[b] - gmax);
    // Chroma: normiert, bei leisen Rahmen ausgeblendet (Stille hat keine Tonhöhe)
    const ch = chroma[k]; let sum = 0; for (const c of ch) sum += c;
    let loud = 0; for (let b = 0; b < BANDS; b++) loud = Math.max(loud, f[b]);
    const g = Math.max(0, Math.min(1, (loud + 45) / 20)) * 40;
    for (let q = 0; q < 12; q++) f[BANDS + q] = sum > 0 ? (ch[q] / sum) * g : 0;
    if (k > 0) for (let b = 0; b < BANDS; b++) f[BANDS + 12 + b] = Math.max(0, f[b] - Math.max(-60, band[k - 1][b] - gmax)) * 1.5;
    F.push(f);
  }
  return F;
}

/**
 * DTW im Band ±W Rahmen mit Steigungsgrenze (Schritte 1:1, 1:2, 2:1, also höchstens doppelt so schnell oder halb so schnell):
 * für jeden Rahmen von B der passende Rahmen von A.
 */
export function dtw(A: Float32Array[], B: Float32Array[], W: number): Float32Array {
  const n = A.length, m = B.length, S = 2 * W + 1, INF = 1e30;
  const D = new Float64Array(n * S).fill(INF), step = new Uint8Array(n * S), dd = new Float32Array(n * S).fill(-1);
  const at = (i: number, j: number) => { const o = j - i + W; return i >= 0 && o >= 0 && o < S && j >= 0 && j < m ? i * S + o : -1; };
  const dist = (i: number, j: number) => {
    const idx = at(i, j); if (idx < 0) return INF;
    if (dd[idx] >= 0) return dd[idx];
    let s2 = 0; const a = A[i], b = B[j]; for (let k = 0; k < a.length; k++) { const d = a[k] - b[k]; s2 += d * d; }
    return (dd[idx] = Math.sqrt(s2 / a.length));
  };
  for (let i = 0; i < n; i++) {
    for (let o = 0; o < S; o++) {
      const j = i + o - W;
      if (j < 0 || j >= m) continue;
      const d = dist(i, j), idx = i * S + o;
      if (i === 0 && j === 0) { D[idx] = d; continue; }
      let best = INF, st = 0;
      const c1 = at(i - 1, j - 1), c2 = at(i - 2, j - 1), c3 = at(i - 1, j - 2);
      if (c1 >= 0 && D[c1] + 2 * d < best) { best = D[c1] + 2 * d; st = 1; }
      if (c2 >= 0 && D[c2] < INF) { const v = D[c2] + 2 * dist(i - 1, j) + d; if (v < best) { best = v; st = 2; } }
      if (c3 >= 0 && D[c3] < INF) { const v = D[c3] + 2 * dist(i, j - 1) + d; if (v < best) { best = v; st = 3; } }
      // Ränder: am Anfang auch gerade Schritte zulassen, damit ein Versatz am Start möglich ist
      if (best === INF) { const u = at(i - 1, j), l = at(i, j - 1); if (u >= 0 && D[u] + d < best) { best = D[u] + d; st = 4; } if (l >= 0 && D[l] + d < best) { best = D[l] + d; st = 5; } }
      D[idx] = best; step[idx] = st;
    }
  }
  const g = new Float32Array(m).fill(NaN);
  let i = n - 1, j = m - 1;
  if (at(i, j) < 0) j = Math.min(m - 1, i + W);
  while (i >= 0 && j >= 0) {
    g[j] = Number.isNaN(g[j]) ? i : (g[j] + i) / 2;
    const idx = at(i, j), st = idx >= 0 ? step[idx] : 0;
    if (st === 1) { i--; j--; }
    else if (st === 2) { g[j - 1] = i - 1.5; i -= 2; j--; }
    else if (st === 3) { g[j - 1] = i - 0.5; i--; j -= 2; }
    else if (st === 4) i--; else if (st === 5) j--; else break;
  }
  for (let k = 0; k < m; k++) if (Number.isNaN(g[k])) g[k] = k > 0 ? g[k - 1] + 1 : 0;
  const sm = new Float32Array(m);
  for (let k = 0; k < m; k++) { let s2 = 0, c = 0; for (let q = Math.max(0, k - 1); q <= Math.min(m - 1, k + 1); q++) { s2 += g[q]; c++; } sm[k] = s2 / c; }
  for (let k = 1; k < m; k++) if (sm[k] < sm[k - 1]) sm[k] = sm[k - 1];
  return sm;
}

/** Zeitkarte Dub → Leitspur (beide Signale in Timeline-Zeit ab t0, gleiche Länge), höchstens maxShift Sekunden Versatz */
export function alignMap(guide: Float32Array, dub: Float32Array, sr: number, t0: number, maxShift = 0.15): (t: number) => number {
  const A = features(guide, sr), B = features(dub, sr), g = dtw(A, B, Math.max(2, Math.round(maxShift / HOP_F)));
  return (t: number) => {
    const f = (t - t0) / HOP_F, k = Math.floor(f), r = f - k;
    if (k < 0) return t; if (k >= g.length - 1) return t0 + g[g.length - 1] * HOP_F + (f - (g.length - 1)) * HOP_F;
    return t0 + (g[k] + (g[k + 1] - g[k]) * r) * HOP_F;
  };
}

// ---------------------------------------------------------------------------
// Ziele

export interface GridTimingOpts { grid: string; /** 0–1 */ strength: number; /** Einsätze, die näher als das am Raster liegen, bleiben (ms) */ tolMs: number; /** nur Phrasenanfänge ziehen, Silben dazwischen wandern mit */ phrasesOnly: boolean }

/** Silben aufs Raster: Stärke, Toleranz; im Phrasen-Modus verschiebt sich jede Phrase als Ganzes */
export function gridTargets(p: Project, onsets: VOnset[], o: GridTimingOpts): TimingEvent[] {
  const k = Math.max(0, Math.min(1, o.strength)), tol = o.tolMs / 1000, out: TimingEvent[] = [];
  let shift = 0;
  for (const on of onsets) {
    if (!o.phrasesOnly || on.phrase) {
      const g = snapGrid(p, on.t, o.grid), d = g - on.t;
      shift = Math.abs(d) <= tol ? 0 : d * k;
      out.push({ t: on.t, target: on.t + shift });
    } else out.push({ t: on.t, target: on.t + shift });
  }
  return monotone(out);
}

/**
 * Auf die Leitspur: jede Silbe des Dubs auf die passende Silbe des Leads (über die Zeitkarte gefunden, dann auf den
 * erkannten Einsatz der Leitspur gelegt), dazwischen Stützpunkte alle 100 ms an hörbaren Stellen entlang der Zeitkarte.
 */
export function guideTargets(onsets: VOnset[], map: (t: number) => number, voiced: (t: number) => boolean, a: number, b: number, strength: number, guideOnsets: VOnset[] = []): TimingEvent[] {
  const k = Math.max(0, Math.min(1, strength)), list: TimingEvent[] = [];
  // Die Zeitkarte ist mitten im Anstieg genauer als am Einsatz selbst (davor ist oft Stille)
  const IN = 0.025;
  for (const on of onsets) {
    const m = map(on.t + IN) - IN;
    let best: number | null = null;
    for (const g of guideOnsets) if (Math.abs(g.t - m) < 0.05 && (best === null || Math.abs(g.t - m) < Math.abs(best - m))) best = g.t;
    list.push({ t: on.t, target: on.t + ((best ?? m) - on.t) * k });
  }
  for (let t = a + 0.05; t < b - 0.05; t += 0.1) if (voiced(t) && !onsets.some((o) => Math.abs(o.t - t) < 0.06)) list.push({ t, target: t + (map(t) - t) * k });
  return monotone(list.sort((x, y) => x.t - y.t));
}

/** Reihenfolge halten und extremes Dehnen vermeiden (Abstand der Ziele mindestens 30 % des Originalabstands) */
function monotone(ev: TimingEvent[]): TimingEvent[] {
  const out: TimingEvent[] = [];
  for (const e of ev) {
    const last = out[out.length - 1];
    if (last) { const min = last.target + Math.max(0.01, (e.t - last.t) * 0.3); if (e.target < min) e.target = min; }
    out.push(e);
  }
  return out;
}
